import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

type BookingModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  url: string
  /** Mount the iframe after the page is idle so the calendar is warm when opened. */
  preload?: boolean
  title?: string
  iframeTitle?: string
  /** Wider, taller panel on desktop — for full booking portals. */
  size?: 'default' | 'large'
}

const easeOut = [0.16, 1, 0.3, 1] as const
const easeIn = [0.4, 0, 0.8, 1] as const

function prefersSaveData() {
  if (typeof navigator === 'undefined') return false
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  return Boolean(connection?.saveData)
}

export default function BookingModal({
  open,
  onOpenChange,
  url,
  preload = false,
  title = 'Book a call',
  iframeTitle = 'Book a discovery call',
  size = 'default',
}: BookingModalProps) {
  const reduceMotion = useReducedMotion()
  const [iframeMounted, setIframeMounted] = useState(false)
  const [iframeReady, setIframeReady] = useState(false)

  useEffect(() => {
    if (open) setIframeMounted(true)
  }, [open])

  useEffect(() => {
    if (!preload || iframeMounted || prefersSaveData()) return

    let idleId = 0
    let timeoutId = 0
    let cancelled = false

    const mount = () => {
      if (!cancelled) setIframeMounted(true)
    }

    const afterLoad = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(mount, { timeout: 2500 })
      } else {
        timeoutId = window.setTimeout(mount, 1500)
      }
    }

    if (document.readyState === 'complete') {
      afterLoad()
    } else {
      window.addEventListener('load', afterLoad, { once: true })
    }

    return () => {
      cancelled = true
      window.removeEventListener('load', afterLoad)
      if (idleId) window.cancelIdleCallback(idleId)
      if (timeoutId) window.clearTimeout(timeoutId)
    }
  }, [preload, iframeMounted])

  const panel = iframeMounted ? (
    <div
      className={cn(
        'fixed flex max-h-[min(92dvh,880px)] flex-col overflow-hidden rounded-2xl bg-white',
        'shadow-[0_24px_80px_-20px_rgba(15,23,42,0.45)]',
        size === 'large'
          ? 'w-[min(100vw-1.5rem,44rem)] lg:w-[min(100vw-4rem,80rem)] lg:max-h-[min(94dvh,1080px)]'
          : 'w-[min(100vw-1.5rem,44rem)]',
        open
          ? 'left-1/2 top-1/2 z-[101] -translate-x-1/2 -translate-y-1/2 pointer-events-auto'
          : 'pointer-events-none left-[-120vw] top-0 z-[-1]',
      )}
      aria-hidden={!open}
    >
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <p className="text-sm font-bold font-heading uppercase tracking-wider text-primary">
          {title}
        </p>
        <div className="flex items-center gap-1">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={open ? 0 : -1}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-100 hover:text-accent transition-colors"
          >
            Open in new tab
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button
            type="button"
            tabIndex={open ? 0 : -1}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 bg-slate-50">
        {!iframeReady ? (
          <div className="absolute inset-0 z-10 animate-pulse bg-slate-100" aria-hidden />
        ) : null}
        <iframe
          title={iframeTitle}
          src={url}
          loading="eager"
          className={cn(
            'w-full border-0 bg-white',
            size === 'large'
              ? 'h-[min(75dvh,720px)] lg:h-[min(86dvh,980px)]'
              : 'h-[min(75dvh,720px)]',
            !open && 'pointer-events-none',
          )}
          allow="camera; microphone; fullscreen"
          tabIndex={open ? 0 : -1}
          onLoad={() => setIframeReady(true)}
          onFocus={(e) => {
            if (!open) e.currentTarget.blur()
          }}
        />
      </div>
    </div>
  ) : null

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <AnimatePresence>
          {open ? (
            <Dialog.Portal forceMount>
              <Dialog.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-[100] bg-primary/55 backdrop-blur-[6px]"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: reduceMotion
                      ? { duration: 0.01 }
                      : { duration: 0.4, ease: easeOut },
                  }}
                  exit={{
                    opacity: 0,
                    transition: reduceMotion
                      ? { duration: 0.01 }
                      : { duration: 0.2, ease: easeIn },
                  }}
                />
              </Dialog.Overlay>
              <Dialog.Content
                className="sr-only"
                aria-describedby={undefined}
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <Dialog.Title>{title}</Dialog.Title>
              </Dialog.Content>
            </Dialog.Portal>
          ) : null}
        </AnimatePresence>
      </Dialog.Root>

      {panel && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  )
}
